"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import { Command, CommandGroup, CommandItem, CommandList } from "./command";
import { menuRowIconClass, popupSurfaceVariants } from "./variants";

export interface SuggestionItem {
  key: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
  /** Full text on hover, for a label that truncates. */
  title?: string;
}

export interface SuggestionGroup {
  heading?: string;
  items: SuggestionItem[];
}

export interface SuggestionListProps {
  /** Accessible name of the list ("Mentions", "Emoji"). */
  label: string;
  groups: SuggestionGroup[];
  /** Index into all items, in group order: the highlighted row. */
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onSelect: (index: number) => void;
  /** Shown while there are no items (loading, no matches). */
  empty?: React.ReactNode;
  /**
   * Positions the list under this element in its own popover. Without it the
   * caller positions the list (the editors' tippy popups).
   */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Anchored only: a pointer down outside the list and its anchor. */
  onDismiss?: () => void;
  testId?: string;
}

const SURFACE = cn(popupSurfaceVariants({ exit: false }), "w-max min-w-56 max-w-96 overflow-hidden");

/**
 * Suggestions for text being typed (an `@` mention, a `:` emoji, an
 * autocomplete): a listbox the field drives. Focus stays in the field, which
 * owns the keys (see `suggestionKeyDown`); the list mirrors `activeIndex`.
 */
export function SuggestionList({
  label,
  groups,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  empty,
  anchorRef,
  onDismiss,
  testId
}: SuggestionListProps) {
  const listRef = React.useRef<HTMLDivElement>(null);

  // Arrow keys move the highlight past the list's max height: keep it in view.
  React.useEffect(() => {
    listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const count = groups.reduce((n, group) => n + group.items.length, 0);
  let index = 0;
  const list = (
    <Command
      ref={listRef}
      shouldFilter={false}
      label={label}
      value={`item-${activeIndex}`}
      onValueChange={(value) => value.startsWith("item-") && onActiveIndexChange(Number(value.slice(5)))}
    >
      <CommandList>
        {/* Not CommandEmpty: cmdk skips it on the first render, and these lists often mount empty (loading). */}
        {count === 0 && empty !== undefined && (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">{empty}</div>
        )}
        {groups.map((group, g) =>
          group.items.length === 0 ? null : (
            <CommandGroup key={group.heading ?? g} heading={group.heading}>
              {group.items.map((item) => {
                const at = index++;
                return (
                  <CommandItem
                    key={item.key}
                    // Every svg at 16px, as in a DropdownMenuItem: CommandItem's own default
                    // misses a lucide icon given a className (it drops the `lucide` class).
                    className={menuRowIconClass}
                    value={`item-${at}`}
                    data-index={at}
                    title={item.title}
                    // Keeps focus (and an editor's suggestion range) in the field through the click.
                    onMouseDown={(e) => e.preventDefault()}
                    onSelect={() => onSelect(at)}
                  >
                    {item.icon}
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )
        )}
      </CommandList>
    </Command>
  );

  if (!anchorRef) {
    return (
      <div className={SURFACE} data-testid={testId}>
        {list}
      </div>
    );
  }
  return (
    <PopoverPrimitive.Root open>
      <PopoverPrimitive.Anchor virtualRef={anchorRef as React.RefObject<HTMLElement>} />
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className={SURFACE}
          data-testid={testId}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // The field's own keydown handles Escape, so an enclosing dialog never sees it.
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (e.target instanceof Node && anchorRef.current?.contains(e.target)) e.preventDefault();
            else onDismiss?.();
          }}
        >
          {list}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/**
 * The list's keys, for the field's keydown: arrows move the highlight (wrapping),
 * Enter and Tab pick it. Returns whether the key was used.
 */
export function suggestionKeyDown(
  key: string,
  { count, activeIndex, setActiveIndex, select }: {
    count: number;
    activeIndex: number;
    setActiveIndex: (index: number) => void;
    select: (index: number) => void;
  }
): boolean {
  if (count === 0) return false;
  if (key === "ArrowDown") {
    setActiveIndex(activeIndex < count - 1 ? activeIndex + 1 : 0);
    return true;
  }
  if (key === "ArrowUp") {
    setActiveIndex(activeIndex > 0 ? activeIndex - 1 : count - 1);
    return true;
  }
  if (key === "Enter" || key === "Tab") {
    select(activeIndex >= 0 && activeIndex < count ? activeIndex : 0);
    return true;
  }
  return false;
}
